<script lang="ts">
  import { flashcards, flashcardActions, filteredFlashcards } from '../stores/flashcards';
  import { Search, Edit, Trash2, Check, X, Volume2, Plus, ChevronLeft  } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import Button from './ui/button.svelte';
  import Card from './ui/card.svelte';
  import { userSettings } from '../stores/settings';

  let { onBack, onAdd, onEdit }: { onBack: () => void, onAdd: () => void, onEdit: (id: string) => void } = $props();

  let searchTerm = $state('');
  let selectedCategory = $state('all');

  let categories = $derived(['all', ...new Set($filteredFlashcards.map(card => card.category?.name).filter(Boolean))]);

  let filteredCards = $derived($filteredFlashcards.filter(card => {
    const matchesSearch =
      card.primary.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.target.includes(searchTerm) ||
      (card.phonetic && card.phonetic.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (card.transliteration && card.transliteration.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' || card.category?.name === selectedCategory;

    return matchesSearch && matchesCategory;
  }));

  function handleToggleLearned(id: string) {
    flashcardActions.toggleLearned(id);
  }

  function handleDelete(id: string) {
    if (confirm('Opravdu chcete smazat tuto kartičku?')) {
      flashcardActions.deleteFlashcard(id);
    }
  }
</script>

<div class="min-h-screen safe-top safe-bottom">
  <div class="container mx-auto px-4 py-8 max-w-2xl">
    <!-- Header -->
    <div class="flex items-center justify-between mb-8">
      <Button onclick={onBack} variant="ghost" class="text-slate-600 hover:text-primary">
        <ChevronLeft  class="w-5 h-5 mr-2" />
        {$_('navigation.back')}
      </Button>
      
      <Button
        onclick={onAdd}
        variant="default"
        class="bg-gradient-to-r from-primary/90 via-secondary/85 to-accent/85 hover:shadow-lifted"
      >
        <Plus class="w-5 h-5 mr-2" />
        {$_('buttons.add')}
      </Button>
    </div>

    <h1 class="text-2xl font-bold text-center mb-6">
      {$_('navigation.list')}
    </h1>

    <!-- Search -->
    <div class="relative mb-6">
      <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <input
        type="text"
        placeholder={$_('form.search_placeholder')}
        bind:value={searchTerm}
        class="form-input w-full pl-10"
      />
    </div>

    <!-- Categories -->
    <div class="flex gap-2 flex-wrap mb-6">
      {#each categories as category}
        <Button
          onclick={() => selectedCategory = category ?? 'all'}
          variant={selectedCategory === category ? "default" : "outline"}
          size="sm"
          class={selectedCategory === category
            ? "bg-gradient-to-r from-primary/85 via-secondary/80 to-accent/80 shadow-soft-sm"
            : "border-transparent shadow-soft-sm hover:bg-white"}
        >
          {category === 'all' ? $_('form.all_categories') : category}
        </Button>
      {/each}
    </div>

    <!-- Cards Grid -->
    <div class="grid gap-4">
      {#each filteredCards as card (card.id)}
        <Card class={`widget-card ${card.isLearned ? 'tone-list ring-2 ring-emerald-300/60' : 'tone-header'} p-4 transition-all hover:-translate-y-1 hover:shadow-lifted`}>
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="font-semibold mb-1">{card.primary}</p>
              <p class="text-lg mb-1" dir="rtl">{card.target}</p>
              {#if $userSettings?.show_ipa_phonetic ? card.phonetic : (card.transliteration || card.phonetic)}
                <p class="text-sm italic">
                  {$userSettings?.show_ipa_phonetic ? card.phonetic : (card.transliteration || card.phonetic)}
                </p>
              {/if}
              {#if card.category}
                <span class="inline-flex items-center mt-2 px-3 py-1 rounded-full text-xs font-semibold shadow-soft-sm">
                  {card.category.name}
                </span>
              {/if}
            </div>
            
            <div class="flex gap-2">
              <Button
                onclick={() => handleToggleLearned(card.id)}
                variant={card.isLearned ? "outline" : "default"}
                size="sm"
                class={card.isLearned ? "border-emerald-500 text-emerald-500 bg-white/80" : "shadow-soft-sm"}
              >
                {#if card.isLearned}
                  <X class="w-4 h-4" />
                {:else}
                  <Check class="w-4 h-4" />
                {/if}
              </Button>
              
              <Button
                onclick={() => onEdit(card.id)}
                variant="ghost"
                size="sm"
                class="text-primary/80 hover:text-primary"
              >
                <Edit class="w-4 h-4" />
              </Button>
              
              <Button
                onclick={() => handleDelete(card.id)}
                variant="ghost"
                size="sm"
                class="text-rose-500 hover:text-rose-400"
              >
                <Trash2 class="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      {/each}
      
      {#if filteredCards.length === 0}
        <Card class="widget-card tone-header p-8 text-center">
          <p class="text-slate-700">
            {searchTerm ? $_('messages.no_search_results') : $_('messages.no_cards')}
          </p>
        </Card>
      {/if}
    </div>
  </div>
</div>
